import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    XMarkIcon,
    UserPlusIcon,
    MagnifyingGlassIcon,
    CalendarDaysIcon,
    UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../ui';
import { getCompanyUsers } from '../../../services/axios/AuthApis/TeamCollabApis';
import { getRiskAssignToApi } from '../../../services/axios/AuthApis/RisksManagementApis';
import { useSelector } from 'react-redux';

const AssignRiskModal = ({ isOpen, onClose, risk, onAssign }) => {
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [dueDate, setDueDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const dropdownRef = useRef(null);
    const debounceTimeout = useRef(null);

    const userDetail = useSelector((state) => state.user);
    let customerMasterId = null;
    if (userDetail?.user?.role === 'customer_admin') {
        customerMasterId = userDetail?.user?.customer_master_id;
    } else if (userDetail?.user?.role == 'security_analyst') { 
        const selectedCompany = useSelector((state) => state.company.selectedCompany);
        customerMasterId = selectedCompany?.company_id;
    }
    const fetchEmployees = useCallback(async (term, page = 1) => {
        if (!customerMasterId) return;
        setSearching(true);
        try {
            const response = await getCompanyUsers(customerMasterId, page, 20, term);
            const results = response.results || [];
            setEmployeeOptions(results);
            if (results.length > 0) setIsDropdownOpen(true);
        } catch (error) {
            console.error('Error fetching employees:', error);
            setEmployeeOptions([]);
        } finally {
            setSearching(false);
        }
    }, [customerMasterId]);

    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        if (!searchTerm.trim()) return; // don't clear — initial load results stay visible
        debounceTimeout.current = setTimeout(() => fetchEmployees(searchTerm), 300);
        return () => clearTimeout(debounceTimeout.current);
    }, [searchTerm, fetchEmployees]);

    const loadExistingAssignment = useCallback(async () => {
        if (!risk) return;
        try {
            const response = await getRiskAssignToApi(risk);
            if (response.success) {
                const assigned = response.data;
                setSelectedEmployee(assigned || null);
                if (response.due_date) {
                    setDueDate(new Date(response.due_date).toISOString().split('T')[0]);
                }
                if (assigned) setSearchTerm(`${assigned.firstname} ${assigned.lastname}`);
            }
        } catch (error) {
            console.error('Error fetching risk assignment:', error);
        }
    }, [risk]);

    useEffect(() => {
        if (isOpen && risk) {
            setIsDropdownOpen(true);
            fetchEmployees();
            loadExistingAssignment();
        } else {
            setSelectedEmployee(null);
            setDueDate('');
            setSearchTerm('');
            setEmployeeOptions([]);
            setErrors({});
        }
    }, [isOpen, risk, loadExistingAssignment]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelectEmployee = (emp) => {
        setSelectedEmployee(emp);
        setSearchTerm(`${emp.firstname} ${emp.lastname}`);
        setIsDropdownOpen(false);
        if (errors.employee) setErrors((p) => ({ ...p, employee: '' }));
    };

    const clearSelection = () => {
        setSelectedEmployee(null);
        setSearchTerm('');
        setIsDropdownOpen(false);
        setEmployeeOptions([]);
    };

    const handleClose = () => {
        clearSelection();
        setDueDate('');
        setErrors({});
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!selectedEmployee) newErrors.employee = 'Please select an employee';
        if (!dueDate) newErrors.dueDate = 'Please select a due date';
        if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

        setLoading(true);
        setTimeout(() => {
            onAssign({
                risk_id: risk,
                employees: selectedEmployee.user_id || selectedEmployee.id,
                due_date: dueDate,
            });
            setLoading(false);
            handleClose();
        }, 1000);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                        onClick={handleClose}
                    />

                    {/* Modal Panel */}
                    <motion.div
                        key="modal"
                        initial={{ opacity: 0, y: -24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.97 }}
                        transition={{ duration: 0.22, ease: 'easeOut' }}
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center gap-4 px-6 py-5 bg-gradient-to-r from-primary-600 to-primary-500 rounded-t-2xl">
                            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                <UserPlusIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-white leading-tight">Assign Risk</h3>
                                <p className="text-xs text-primary-100 mt-0.5">Assign this risk to a team member</p>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-shrink-0 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <form onSubmit={handleSubmit} id="assign-form" className="flex-1 overflow-y-auto">
                            <div className="px-6 py-5 space-y-5">

                                {/* Assign To */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Assign To <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative" ref={dropdownRef}>
                                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                                        <input
                                            type="text"
                                            placeholder="Search employee…"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setIsDropdownOpen(true);
                                                if (selectedEmployee && e.target.value === '') clearSelection();
                                            }}
                                            onFocus={() => setIsDropdownOpen(true)}
                                            className={`w-full pl-9 pr-8 py-2.5 border ${errors.employee ? 'border-red-400' : 'border-gray-200'} rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white`}
                                        />
                                        {(selectedEmployee || searchTerm) && (
                                            <button
                                                type="button"
                                                onClick={clearSelection}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                                            >
                                                <XMarkIcon className="w-3.5 h-3.5" />
                                            </button>
                                        )}

                                        {/* Dropdown */}
                                        <AnimatePresence>
                                            {isDropdownOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -4 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -4 }}
                                                    transition={{ duration: 0.12 }}
                                                    className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto"
                                                >
                                                    {searching ? (
                                                        <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
                                                            <div className="w-4 h-4 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
                                                            Searching…
                                                        </div>
                                                    ) : employeeOptions.length > 0 ? (
                                                        employeeOptions.map((emp) => (
                                                            <button
                                                                key={emp.employee_id}
                                                                type="button"
                                                                onClick={() => handleSelectEmployee(emp)}
                                                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary-50 transition-colors ${selectedEmployee?.user_id === emp.user_id ? 'bg-primary-50' : ''}`}
                                                            >
                                                                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                                                    <span className="text-xs font-semibold text-primary-600">
                                                                        {emp.firstname?.charAt(0)}{emp.lastname?.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className={`text-sm font-medium truncate ${selectedEmployee?.user_id === emp.user_id ? 'text-primary-700' : 'text-gray-800'}`}>
                                                                        {emp.firstname} {emp.lastname}
                                                                    </p>
                                                                    {emp.department && (
                                                                        <p className="text-xs text-gray-400 truncate">{emp.department}</p>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        ))
                                                    ) : searchTerm ? (
                                                        <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                                                            <UserCircleIcon className="w-4 h-4" />
                                                            No employees found
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                                                            <MagnifyingGlassIcon className="w-4 h-4" />
                                                            Type to search employees…
                                                        </div>
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                    {errors.employee && <p className="mt-1 text-xs text-red-500">{errors.employee}</p>}
                                </div>

                                {/* Selected employee chip */}
                                {selectedEmployee && (
                                    <div className="flex items-center gap-3 px-4 py-3 bg-primary-50 border border-primary-200 rounded-xl">
                                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                            <span className="text-xs font-semibold text-primary-600">
                                                {selectedEmployee.firstname?.charAt(0)}{selectedEmployee.lastname?.charAt(0)}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-primary-700 truncate">
                                                {selectedEmployee.firstname} {selectedEmployee.lastname}
                                            </p>
                                            {selectedEmployee.department && (
                                                <p className="text-xs text-primary-500 truncate">{selectedEmployee.department}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Due Date */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Due Date <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <CalendarDaysIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                        <input
                                            type="date"
                                            value={dueDate}
                                            onChange={(e) => {
                                                setDueDate(e.target.value);
                                                if (errors.dueDate) setErrors((p) => ({ ...p, dueDate: '' }));
                                            }}
                                            min={new Date().toISOString().split('T')[0]}
                                            className={`w-full pl-9 pr-3 py-2.5 border ${errors.dueDate ? 'border-red-400' : 'border-gray-200'} rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white`}
                                        />
                                    </div>
                                    {errors.dueDate && <p className="mt-1 text-xs text-red-500">{errors.dueDate}</p>}
                                </div>

                            </div>
                        </form>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl">
                            <p className="text-xs text-gray-400">
                                <span className="text-red-400">*</span> Required fields
                            </p>
                            <div className="flex items-center gap-3">
                                <Button type="button" variant="secondary" size="sm" onClick={handleClose} disabled={loading}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    form="assign-form"
                                    variant="primary"
                                    size="sm"
                                    disabled={loading || !dueDate || !selectedEmployee}
                                    loading={loading}
                                >
                                    {loading ? 'Assigning…' : 'Assign Risk'}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default AssignRiskModal;
